import { Component } from "react";
import type { ReactNode } from "react";

import { reportClientError } from "~/utils/report-client-error";

import {
  readLoadedBuildId,
  shouldReloadForModuleError,
} from "./error-boundries/error-utils";
import {
  type GuardStorage,
  type OverlayFailure,
  classifyOverlayFailure,
  persistReloadGuard,
  safeSessionStorage,
} from "./overlay-retry";

/**
 * Containment boundary for the lazy overlay chrome (PR #1042 blocker 3).
 *
 * COM-189 waves 4/4B made every closed-at-paint overlay (auth drawer, mobile
 * nav, minicart popover, account flyout/drawer, search overlay, ATC modal,
 * balance/welcome modals) a `lazy()` mount, and `useOverlaysReady` starts
 * those imports within ~2.5s of hydration with no user intent. A rejected
 * chunk import propagates during render, so WITHOUT a boundary one transient
 * failure (network blip, mid-deploy asset window) unmounted a fully working
 * page into RootErrorBoundary — a forced reload, and the black offline screen
 * on a second same-build failure.
 *
 * This boundary contains the failure to the overlay itself: the page keeps
 * working, the overlay degrades to `fallback` (its SSR-visible trigger, or
 * nothing — exactly what renders before the chunk anyway), and the failure is
 * beaconed once via reportClientError so chunk-failure rates are observable
 * per overlay after each deploy. Same defense-in-depth pattern as
 * AutocompleteErrorBoundary (cio-autocomplete-boundary.tsx, SFCC-11911).
 *
 * The latched state is sticky by default (no retry loop): a full
 * navigation/reload re-mounts a fresh boundary, which is also when a
 * mid-deploy asset window has healed. Two opt-in seams let a caller retry on
 * the shopper's NEXT explicit ask instead (PR #1141, COM-189 wave 4 follow-up
 * — see `overlay-retry.ts` for why a failed `import()` cannot simply be
 * re-rendered):
 * `onError` reports what was caught (and whether it was a module-load
 * failure), and a change of `retryToken` while latched clears the latch and
 * renders `children` again.
 *
 * `reloadOnModuleLoadError` (opt-in, default off — no behaviour change for the
 * existing mounts): for an overlay the shopper explicitly asked for (the Quick
 * View — C6, the one lazy modal the B3 sweep never covered), degrading to a
 * fallback is not enough when the chunk failed because a deploy rotated the
 * bundle. With this set, a module-load error takes the SFCC-11887 lane the
 * RootErrorBoundary already uses: one guarded document reload per BUILD
 * (`shouldReloadForModuleError`, keyed on the loaded manifest version so a
 * genuinely missing chunk cannot loop), beaconed before the reload so it is
 * countable. Any other error, or a spent guard, renders `fallback` as before.
 * The automatic reload additionally requires the guard write to READ BACK:
 * when sessionStorage is blocked the guard cannot persist, so the boundary
 * renders `fallback` (whose manual Reload button is the shopper's path) rather
 * than risk an unbounded reload loop.
 */
export class OverlayBoundary extends Component<
  {
    overlay: string;
    fallback?: ReactNode;
    children: ReactNode;
    reloadOnModuleLoadError?: boolean;
    /** Test seam; defaults to a document reload. */
    reloadDocument?: () => void;
    /** Test seam; defaults to `window.sessionStorage` (null when blocked). */
    guardStorage?: () => GuardStorage;
    /** Called once per caught error, after the beacon (and after any
     *  automatic reload decision). Lets the owner of the trigger know the
     *  overlay is parked so its next tap can retry (`overlay-retry.ts`). */
    onError?: (failure: OverlayFailure) => void;
    /** Bump to clear a latched error and render `children` again. Only
     *  meaningful for non-module errors — a rejected `import()` is cached by
     *  both React.lazy and the module map, so that case needs a reload. */
    retryToken?: number;
  },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { retryToken?: number }) {
    if (
      this.state.hasError &&
      this.props.retryToken !== undefined &&
      this.props.retryToken !== prevProps.retryToken
    ) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown) {
    const buildId = readLoadedBuildId();
    const storage = (this.props.guardStorage ?? safeSessionStorage)();
    const wantsReload =
      this.props.reloadOnModuleLoadError === true &&
      typeof window !== "undefined" &&
      shouldReloadForModuleError(error, { buildId, storage });
    // Mark BEFORE reloading, against the build we are reloading AWAY FROM
    // (same contract as RootErrorBoundary): a chunk still missing after the
    // reload lands on the same build id and gets no second attempt. The
    // read-back is load-bearing — no persisted guard, no automatic reload.
    const reload = wantsReload && persistReloadGuard(storage, buildId);
    // `dedupeKey`: reloaded vs reload-skipped are different outcomes of the
    // same error and must both reach the log (COM-448).
    reportClientError(
      "lazy-overlay",
      error,
      {
        overlay: this.props.overlay,
        reload,
        ...(wantsReload && !reload
          ? { reloadSkipped: "guard-not-persisted" }
          : {}),
      },
      {
        dedupeKey: reload
          ? "reload"
          : wantsReload
            ? "reload-skipped"
            : "no-reload",
      },
    );
    this.props.onError?.(classifyOverlayFailure(error));
    if (!reload) return;
    (this.props.reloadDocument ?? (() => window.location.reload()))();
  }

  render() {
    return this.state.hasError
      ? (this.props.fallback ?? null)
      : this.props.children;
  }
}
